'use client';

import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from "next/navigation";
import React, { useEffect, useRef, useState } from 'react';
import {
  FiArrowRight,
  FiBarChart2,
  FiBookOpen,
  FiChevronDown,
  FiCode,
  FiCoffee,
  FiFileText,
  FiHeart,
  FiHome,
  FiImage,
  FiLayout,
  FiMenu,
  FiMessageSquare,
  FiMic,
  FiShoppingBag,
  FiTrendingUp,
  FiX
} from 'react-icons/fi';
import { Button } from '../ui';
import { US_SITE_ROUTES } from '@/lib/geo/us-site-routes';

function performanceMarketingHref(isUSPage: boolean): string {
  return isUSPage
    ? "/us/performance-marketing"
    : "/performance-marketing";
}

type NavItem = {
  href: string;
  label: string;
  hasDropdown?: boolean;
  dropdownSections?: {
    title: string;
    overviewHref: string;
    items: {
      href: string;
      label: string;
      icon: React.ComponentType<{ className?: string }>;
    }[];
  }[];
  dropdownItems?: {
    href: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
};

type DropdownSection = {
  title: string;
  overviewHref: string;
  items: {
    href: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
};

type DropdownItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

type Sublink = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  const pathname = usePathname();

  const isUSPage =
    pathname === "/us" ||
    pathname.startsWith("/us/");

  /**
   * Prefix href with /us only when:
   *  1. The visitor is on a US page (isUSPage)
   *  2. The route is registered in US_SITE_ROUTES
   * Routes not in US_SITE_ROUTES always resolve to the global path.
   */
  const withRegion = React.useCallback((href: string) => {
    if (!href.startsWith("/")) return href;
    if (href.startsWith("/us")) return href;
    if (isUSPage && US_SITE_ROUTES.includes(href)) return `/us${href}`;
    return href;
  }, [isUSPage]);

  const navItems: NavItem[] = React.useMemo(
    () => [
      { href: withRegion('/about-us'), label: 'About Us' },
      { href: withRegion('/brand-development-lifecycle'), label: 'BDLC' },
      { href: withRegion('/a-to-z-of-branding'), label: 'A to Z of Branding' },
      {
        href: withRegion('/services'),
        label: 'Services',
        hasDropdown: true,
        dropdownSections: [
          {
            title: 'Design/Development-as-a-Service',
            overviewHref: withRegion('/design-development-as-a-service'),
            items: [
              { href: withRegion('/ui-ux-design'), label: 'UI/UX Design', icon: FiLayout },
              { href: withRegion('/website-development'), label: 'Website Development', icon: FiCode },
              { href: withRegion('/graphic-design'), label: 'Graphic Design', icon: FiImage },
            ]
          },
          {
            title: 'Growth-as-a-Service',
            overviewHref: withRegion('/growth-as-a-service'),
            items: [
              { href: withRegion('/organic-visibility'), label: 'Organic Visibility', icon: FiTrendingUp },
              // {
              //   href: performanceMarketingHref(isUSPage),
              //   label: 'Performance Marketing',
              //   icon: FiBarChart2,
              // },
              { href: withRegion('/content-marketing'), label: 'Content Marketing', icon: FiFileText },
               { href: withRegion('/performance-marketing'), label: 'Performance Marketing', icon: FiBarChart2 },
            ]
          },
          {
            title: 'AI-as-a-Service',
            overviewHref: withRegion('/ai-as-a-service'),
            items: []
          },
          {
            title: 'Staff-as-a-Service',
            overviewHref: withRegion('/staff-as-a-service'),
            items: []
          },
        ],
      },
      {
        href: withRegion('/industries'),
        label: 'Industries',
        hasDropdown: true,
        dropdownItems: [
          { href: withRegion('/industries/healthcare'), label: 'HealthCare', icon: FiHeart },
          { href: withRegion('/industries/real-estate'), label: 'Real Estate', icon: FiHome },
          { href: withRegion('/industries/e-commerce'), label: 'E-Commerce', icon: FiShoppingBag },
          { href: withRegion('/industries/hospitality'), label: 'Hospitality', icon: FiCoffee },
          { href: withRegion('/industries/ed-tech'), label: 'Edtech', icon: FiBookOpen },
        ]
      },
      {
        href: withRegion('/success-stories'),
        label: 'Success Stories',
        hasDropdown: true,
        dropdownItems: [
          { href: withRegion('/case-study'), label: 'Case Studies', icon: FiBookOpen },
          { href: withRegion('/testimonial'), label: 'Testimonials', icon: FiMessageSquare },
        ]
      },
      {
        href: withRegion('/resources'),
        label: 'Resources',
        hasDropdown: true,
        dropdownItems: [
          { href: withRegion('/blogs'), label: 'Blogs', icon: FiBookOpen },
          { href: withRegion('/news-pr'), label: 'News-PR', icon: FiMic },
          { href: withRegion('/thought-leadership'), label: 'Thought-Leadership', icon: FiFileText },
        ]
      },
    ],
    [withRegion]
  );

  const toggleMenu = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setIsMenuOpen(prev => {
      const newState = !prev;
      if (!newState) {
        setActiveDropdown(null);
      }
      return newState;
    });
  };

  const handleMouseEnter = (itemLabel: string) => {
    if (!isMobile) {
      setActiveDropdown(itemLabel);
    }
  };

  const handleContainerMouseLeave = () => {
    if (!isMobile) {
      setActiveDropdown(null);
    }
  };

  // Responsive breakpoint detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1024);

      if (width >= 1024 && isMenuOpen) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 4);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const mobileButton = document.querySelector('[data-mobile-menu-button]');
      if (mobileButton && mobileButton.contains(target)) {
        return;
      }

      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target)
      ) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <nav
      className={
        `fixed top-0 z-[99999] w-full transition-[background-color,backdrop-filter,box-shadow,transform] duration-300 ` +
        (hasScrolled
          ? 'bg-white/70 backdrop-blur-md shadow-sm border-b border-gray-200/60'
          : 'bg-transparent')
      }
    >
      <div className="sr-only">
        <Link href={withRegion('/design-development-as-a-service')}>Design/Development-as-a-Service</Link>
        <Link href={withRegion('/ui-ux-design')}>UI/UX Design</Link>
        <Link href={withRegion('/website-development')}>Website Development</Link>
        <Link href={withRegion('/graphic-design')}>Graphic Design</Link>

        <Link href={withRegion('/growth-as-a-service')}>Growth-as-a-Service</Link>
        <Link href={withRegion('/organic-visibility')}>Organic Visibility</Link>
        <Link href={performanceMarketingHref(isUSPage)}>Performance Marketing</Link>
        <Link href={withRegion('/content-marketing')}>Content Marketing</Link>

        <Link href={withRegion('/ai-as-a-service')}>AI-as-a-Service</Link>
        <Link href={withRegion('/staff-as-a-service')}>Staff-as-a-Service</Link>

        <Link href={withRegion('/industries/healthcare')}>HealthCare</Link>
        <Link href={withRegion('/industries/real-estate')}>Real Estate</Link>
        <Link href={withRegion('/industries/e-commerce')}>E-Commerce</Link>
        <Link href={withRegion('/industries/hospitality')}>Hospitality</Link>
        <Link href={withRegion('/industries/ed-tech')}>Edtech</Link>

        <Link href={withRegion('/blogs')}>Blogs</Link>
        <Link href={withRegion('/news-pr')}>News-PR</Link>
        <Link href={withRegion('/thought-leadership')}>Thought-Leadership</Link>

        <Link href={withRegion('/case-study')}>Case Studies</Link>
        <Link href={withRegion('/testimonial')}>Testimonials</Link>
      </div>

      <div className="custom-container mx-auto px-4 sm:px-6 lg:px-8" onMouseLeave={handleContainerMouseLeave}>
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            className="flex-shrink-0"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <Link href={isUSPage ? "/us" : "/"} className="flex items-center">
              <Image
                src="/logo/logo.svg"
                alt="Make My Brand"
                width={150}
                height={38}
                className="h-9 w-auto"
                priority
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation and CTA Buttons */}
          <div className="hidden xl:flex items-center space-x-12">
            {/* Navigation Links */}
            <motion.div
              className="flex items-center space-x-8"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
            >
              {navItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + (index * 0.05), ease: "easeOut" }}
                >
                  {item.hasDropdown ? (
                    <>
                      <div
                        className="flex items-center space-x-1 text-gray-700 hover:text-[#d53333] text-sm font-medium transition-colors duration-200 cursor-pointer"
                        onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                      >
                        <span>{item.label}</span>
                        <motion.div
                          animate={{ rotate: activeDropdown === item.label ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <FiChevronDown className="w-4 h-4" />
                        </motion.div>
                      </div>
                      <AnimatePresence>
                        {activeDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className={
                              item.dropdownSections
                                ? 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-[720px] bg-white border border-gray-200 rounded-2xl shadow-xl p-4 sm:p-6 z-[400]'
                                : item.label === 'Industries'
                                  ? 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-[500px] bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-[400]'
                                  : 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-80 bg-white border border-gray-200 rounded-xl shadow-lg py-3 z-[400]'
                            }
                          >
                            {item.dropdownSections ? (
                              <div className="space-y-6">
                                {item.dropdownSections.map((section: DropdownSection, sectionIndex: number) => (
                                  <motion.div
                                    key={section.title}
                                    className="space-y-3"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: sectionIndex * 0.1 }}
                                  >
                                    <Link
                                      href={section.overviewHref}
                                      className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-[#d53333]"
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {section.title}
                                      <motion.div
                                        whileHover={{ x: 2 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <FiArrowRight className="w-4 h-4" />
                                      </motion.div>
                                    </Link>
                                    {section.items?.length > 0 && (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {section.items.map((sublink: Sublink, subIndex: number) => (
                                          <motion.div
                                            key={sublink.href}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.18, delay: (sectionIndex * 0.05) + (subIndex * 0.03) }}
                                          >
                                            <Link
                                              href={sublink.href}
                                              className="group flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                              onClick={() => setActiveDropdown(null)}
                                            >
                                              <motion.span
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors ring-1 ring-transparent group-hover:bg-[#d53333]/10 group-hover:text-[#d53333] group-hover:ring-[#d53333]/20"
                                                whileHover={{ scale: 1.1 }}
                                                transition={{ duration: 0.2 }}
                                              >
                                                {sublink.icon ? React.createElement(sublink.icon, { className: 'w-4 h-4' }) : '•'}
                                              </motion.span>
                                              <span className="flex-1 group-hover:text-[#d53333]">{sublink.label}</span>
                                              <motion.div
                                                initial={{ opacity: 0, x: -4 }}
                                                whileHover={{ opacity: 1, x: 0 }}
                                                transition={{ duration: 0.2 }}
                                              >
                                                <FiArrowRight className="w-4 h-4 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 text-gray-400 group-hover:text-[#d53333]" />
                                              </motion.div>
                                            </Link>
                                          </motion.div>
                                        ))}
                                      </div>
                                    )}
                                  </motion.div>
                                ))}
                                {item.dropdownItems?.length ? (
                                  <motion.div
                                    className="pt-2 border-t border-gray-100"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                  >
                                    {item.dropdownItems.map((dropdownItem: DropdownItem, idx: number) => (
                                      <motion.div
                                        key={dropdownItem.href}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.18, delay: idx * 0.03 }}
                                      >
                                        <Link
                                          href={dropdownItem.href}
                                          className="group flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                          onClick={() => setActiveDropdown(null)}
                                        >
                                          <motion.span
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors ring-1 ring-transparent group-hover:bg-[#d53333]/10 group-hover:text-[#d53333] group-hover:ring-[#d53333]/20"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-4 h-4' }) : '•'}
                                          </motion.span>
                                          <span className="flex-1 group-hover:text-[#d53333]">{dropdownItem.label}</span>
                                          <motion.div
                                            initial={{ opacity: 0, x: -4 }}
                                            whileHover={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            <FiArrowRight className="w-4 h-4 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 text-gray-400 group-hover:text-[#d53333]" />
                                          </motion.div>
                                        </Link>
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                ) : null}
                              </div>
                            ) : (
                              <div className={item.label === 'Industries' ? 'grid grid-cols-2 gap-2' : ''}>
                                {item.dropdownItems?.map((dropdownItem: DropdownItem, idx: number) => (
                                  <motion.div
                                    key={dropdownItem.href}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.18, delay: idx * 0.03 }}
                                  >
                                    <Link
                                      href={dropdownItem.href}
                                      className="group flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-xl"
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      <motion.span
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors ring-1 ring-transparent group-hover:bg-[#d53333]/10 group-hover:text-[#d53333] group-hover:ring-[#d53333]/20"
                                        whileHover={{ scale: 1.1 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-4 h-4' }) : '•'}
                                      </motion.span>
                                      <span className="flex-1 group-hover:text-[#d53333]">{dropdownItem.label}</span>
                                      <motion.div
                                        initial={{ opacity: 0, x: -4 }}
                                        whileHover={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <FiArrowRight className="w-4 h-4 opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0 text-gray-400 group-hover:text-[#d53333]" />
                                      </motion.div>
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      href={item.href}
                      className="text-gray-700 hover:text-[#d53333] text-sm font-medium transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              className="flex items-center space-x-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="primary" href={withRegion('/contact-us')}>
                  Contact us
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Tablet Navigation - Simplified */}
          <div className="hidden lg:flex xl:hidden items-center space-x-6">
            <motion.div
              className="flex items-center space-x-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {navItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  className="relative"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 + (index * 0.05) }}
                >
                  {item.hasDropdown ? (
                    <>
                      <div
                        className="flex items-center space-x-1 text-gray-700 hover:text-[#d53333] text-sm font-medium transition-colors duration-200 cursor-pointer"
                        onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                      >
                        <span>{item.label}</span>
                        <motion.div
                          animate={{ rotate: activeDropdown === item.label ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <FiChevronDown className="w-4 h-4" />
                        </motion.div>
                      </div>
                      <AnimatePresence>
                        {activeDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className={
                              item.dropdownSections
                                ? 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-[600px] bg-white border border-gray-200 rounded-2xl shadow-xl p-4 sm:p-6 z-[400]'
                                : item.label === 'Industries'
                                  ? 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-[450px] bg-white border border-gray-200 rounded-xl shadow-lg p-4 z-[400]'
                                  : 'absolute left-1/2 transform -translate-x-1/2 top-full mt-3 w-72 bg-white border border-gray-200 rounded-xl shadow-lg py-3 z-[400]'
                            }
                          >
                            {item.dropdownSections ? (
                              <div className="space-y-4">
                                {item.dropdownSections.map((section: DropdownSection, sectionIndex: number) => (
                                  <motion.div
                                    key={section.title}
                                    className="space-y-2"
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: sectionIndex * 0.1 }}
                                  >
                                    <Link
                                      href={section.overviewHref}
                                      className="group inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-[#d53333]"
                                      onClick={() => setActiveDropdown(null)}
                                    >
                                      {section.title}
                                      <motion.div
                                        whileHover={{ x: 2 }}
                                        transition={{ duration: 0.2 }}
                                      >
                                        <FiArrowRight className="w-4 h-4" />
                                      </motion.div>
                                    </Link>
                                    {section.items?.length > 0 && (
                                      <div className="grid grid-cols-1 gap-2">
                                        {section.items.map((sublink: Sublink, subIndex: number) => (
                                          <motion.div
                                            key={sublink.href}
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.18, delay: (sectionIndex * 0.05) + (subIndex * 0.03) }}
                                          >
                                            <Link
                                              href={sublink.href}
                                              className="group flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                              onClick={() => setActiveDropdown(null)}
                                            >
                                              <motion.span
                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors ring-1 ring-transparent group-hover:bg-[#d53333]/10 group-hover:text-[#d53333] group-hover:ring-[#d53333]/20"
                                                whileHover={{ scale: 1.1 }}
                                                transition={{ duration: 0.2 }}
                                              >
                                                {sublink.icon ? React.createElement(sublink.icon, { className: 'w-4 h-4' }) : '•'}
                                              </motion.span>
                                              <span className="flex-1 group-hover:text-[#d53333]">{sublink.label}</span>
                                            </Link>
                                          </motion.div>
                                        ))}
                                      </div>
                                    )}
                                  </motion.div>
                                ))}
                                {item.dropdownItems?.length ? (
                                  <motion.div
                                    className="pt-2 border-t border-gray-100"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.2 }}
                                  >
                                    {item.dropdownItems.map((dropdownItem: DropdownItem, idx: number) => (
                                      <motion.div
                                        key={dropdownItem.href}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.18, delay: idx * 0.03 }}
                                      >
                                        <Link
                                          href={dropdownItem.href}
                                          className="group flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                          onClick={() => setActiveDropdown(null)}
                                        >
                                          <motion.span
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-gray-100 text-gray-500 transition-colors ring-1 ring-transparent group-hover:bg-[#d53333]/10 group-hover:text-[#d53333] group-hover:ring-[#d53333]/20"
                                            whileHover={{ scale: 1.1 }}
                                            transition={{ duration: 0.2 }}
                                          >
                                            {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-4 h-4' }) : '•'}
                                          </motion.span>
                                          <span className="flex-1 group-hover:text-[#d53333]">{dropdownItem.label}</span>
                                        </Link>
                                      </motion.div>
                                    ))}
                                  </motion.div>
                                ) : null}
                              </div>
                            ) : (
                              <div className={item.label === 'Industries' ? 'grid grid-cols-2 gap-2' : ''}>
                                {item.dropdownItems?.map((dropdownItem: DropdownItem, dropdownIndex: number) => (
                                  <motion.div
                                    key={dropdownItem.href}
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                      duration: 0.2,
                                      delay: dropdownIndex * 0.05
                                    }}
                                  >
                                    <Link
                                      href={dropdownItem.href}
                                      className="group flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-[#d53333] hover:bg-gray-50 rounded-md"
                                      onClick={() => {
                                        setIsMenuOpen(false);
                                        setActiveDropdown(null);
                                      }}
                                    >
                                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                                        {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-3 h-3' }) : '•'}
                                      </span>
                                      {dropdownItem.label}
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      href={item.href}
                      className="text-gray-700 hover:text-[#d53333] text-sm font-medium transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
                  )}
                </motion.div>
              ))}
            </motion.div>
            <motion.div
              className="flex items-center space-x-2"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="primary" href={withRegion('/contact-us')} size="sm">
                  Contact us
                </Button>
              </motion.div>
            </motion.div>
          </div>

          {/* Mobile menu button */}
          <motion.div
            className="lg:hidden"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <motion.button
              onClick={(e) => toggleMenu(e)}
              className="text-gray-700 hover:text-[#d53333] focus:outline-none focus:text-[#d53333] transition-colors duration-200 p-2"
              aria-label="Toggle menu"
              data-mobile-menu-button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                {isMenuOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiX size={24} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <FiMenu size={24} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>

        {/* Mobile Navigation Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              ref={mobileMenuRef}
              className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-[99998]"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <motion.div
                className="px-4 py-4 space-y-2 max-h-[calc(100vh-4rem)] overflow-y-auto"
                initial={{ y: -20 }}
                animate={{ y: 0 }}
                exit={{ y: -20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{
                      duration: 0.3,
                      delay: index * 0.05,
                      ease: "easeOut"
                    }}
                  >
                    {item.hasDropdown ? (
                      <div className="space-y-1">
                        <motion.button
                          onClick={() => setActiveDropdown(activeDropdown === item.label ? null : item.label)}
                          className="w-full flex items-center justify-between text-gray-700 hover:text-[#d53333] hover:bg-gray-50 px-3 py-3 rounded-lg text-base font-medium transition-all duration-200"
                          whileTap={{ scale: 0.98 }}
                        >
                          <span>{item.label}</span>
                          <motion.div
                            animate={{ rotate: activeDropdown === item.label ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <FiChevronDown className="w-4 h-4" />
                          </motion.div>
                        </motion.button>
                        <AnimatePresence>
                          {activeDropdown === item.label && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="ml-4 space-y-1 border-l-2 border-gray-100 pl-4"
                            >
                              {item.dropdownSections ? (
                                <div className="space-y-3">
                                  {item.dropdownSections.map((section: DropdownSection, sectionIndex: number) => (
                                    <motion.div
                                      key={section.title}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ duration: 0.2, delay: sectionIndex * 0.1 }}
                                      className="space-y-2"
                                    >
                                      <Link
                                        href={section.overviewHref}
                                        className="block px-3 py-2 text-sm font-semibold text-gray-900 hover:text-[#d53333] hover:bg-gray-50 rounded-md"
                                        onClick={() => {
                                          setIsMenuOpen(false);
                                          setActiveDropdown(null);
                                        }}
                                      >
                                        {section.title}
                                      </Link>
                                      {section.items?.map((sublink: Sublink, subIndex: number) => (
                                        <motion.div
                                          key={sublink.href}
                                          initial={{ opacity: 0, x: -10 }}
                                          animate={{ opacity: 1, x: 0 }}
                                          transition={{ duration: 0.2, delay: (sectionIndex * 0.1) + (subIndex * 0.05) }}
                                        >
                                          <Link
                                            href={sublink.href}
                                            className="flex items-center gap-3 px-6 py-2 text-sm text-gray-600 hover:text-[#d53333] hover:bg-gray-50 rounded-md"
                                            onClick={() => {
                                              setIsMenuOpen(false);
                                              setActiveDropdown(null);
                                            }}
                                          >
                                            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                                              {sublink.icon ? React.createElement(sublink.icon, { className: 'w-3 h-3' }) : '•'}
                                            </span>
                                            {sublink.label}
                                          </Link>
                                        </motion.div>
                                      ))}
                                    </motion.div>
                                  ))}
                                  {item.dropdownItems?.map((dropdownItem: DropdownItem, idx: number) => (
                                    <motion.div
                                      key={dropdownItem.href}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ duration: 0.2, delay: idx * 0.05 }}
                                    >
                                      <Link
                                        href={dropdownItem.href}
                                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-[#d53333] hover:bg-gray-50 rounded-md"
                                        onClick={() => {
                                          setIsMenuOpen(false);
                                          setActiveDropdown(null);
                                        }}
                                      >
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                                          {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-3 h-3' }) : '•'}
                                        </span>
                                        {dropdownItem.label}
                                      </Link>
                                    </motion.div>
                                  ))}
                                </div>
                              ) : (
                                <div className={item.label === 'Industries' ? 'grid grid-cols-2 gap-2' : ''}>
                                  {item.dropdownItems?.map((dropdownItem: DropdownItem, dropdownIndex: number) => (
                                    <motion.div
                                      key={dropdownItem.href}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{
                                        duration: 0.2,
                                        delay: dropdownIndex * 0.05
                                      }}
                                    >
                                      <Link
                                        href={dropdownItem.href}
                                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-[#d53333] hover:bg-gray-50 rounded-md"
                                        onClick={() => {
                                          setIsMenuOpen(false);
                                          setActiveDropdown(null);
                                        }}
                                      >
                                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                                          {dropdownItem.icon ? React.createElement(dropdownItem.icon, { className: 'w-3 h-3' }) : '•'}
                                        </span>
                                        {dropdownItem.label}
                                      </Link>
                                    </motion.div>
                                  ))}
                                </div>
                              )}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <motion.div whileTap={{ scale: 0.98 }}>
                        <Link
                          href={item.href}
                          className="text-gray-700 hover:text-[#d53333] hover:bg-gray-50 block px-3 py-3 rounded-lg text-base font-medium transition-all duration-200"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
                <motion.div
                  className="pt-4 pb-2 space-y-3 border-t border-gray-100 mt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{
                    duration: 0.3,
                    delay: navItems.length * 0.05,
                    ease: "easeOut"
                  }}
                >
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button
                      variant="primary"
                      href={withRegion('/contact-us')}
                      fullWidth
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Contact us
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;